/**
 * GROW A TREE — one grammar, driven entirely by a resolved parameter set.
 *
 * There is deliberately no `if (species === 'oak')` anywhere below. A species
 * is `profile.ts`'s numbers and one `FoliageStyle`; everything a viewer would
 * call "that is an oak" comes out of the numbers. That is the whole test of
 * whether the profiles are real: if a species needs a special case here, its
 * profile is missing a parameter, and the parameter is the fix.
 *
 * ## The grammar
 *
 *     bole  →  primaries  →  order 2  →  order 3 …  →  foliage on the tips
 *
 * with four rules that carry most of the character:
 *
 *  1. **The bole is a chain, not a cylinder.** It leans, and it wanders as it
 *     rises (`sinuosity`), and it tapers geometrically. A straight bole is a
 *     mast; a real one has a direction it changed its mind about.
 *  2. **Apical dominance decides where the wood goes.** At 1 the leader takes
 *     nearly all the length and the limbs are stubs off it — which IS a
 *     sapling, and is why a sapling here is not a small adult. At 0 the
 *     leader is one limb among equals, which is an old broadleaf. The curve
 *     between them is the species' life.
 *  3. **Every order is a `bough`** — it curves as it thins. Straight sticks
 *     read as a candelabra at any count (`TREES.md` §0).
 *  4. **Foliage grows where a branch ENDED.** Never scattered near a centre,
 *     never clamped to a ceiling: the branch explains the leaf, and if the
 *     canopy has a shape it is because the branches do.
 *
 * ## Determinism
 *
 * Everything is drawn from one `mulberry32` stream seeded by the caller's
 * seed. Same (species, seed, age, environment) → the same tree, on every
 * machine and in every worker. Nothing reads a clock or `Math.random`.
 */
import * as THREE from 'three';
import { clamp, lerp, mulberry32 } from '@/core/rng';
import {
  BARK, bough, faceted, leafCards, lobe, merge, part,
} from '@/render/tree/parts';
import { expressed, type PlantPhenotype } from '@/render/phenotype';
import {
  NEUTRAL_ENVIRONMENT, curveAt, pick, stageAt,
  type StageName, type TreeEnvironment, type TreeSpeciesProfile,
} from './profile';

export interface GrowParams {
  species: TreeSpeciesProfile;
  /** The individual. Any integer; the same one always grows the same tree. */
  seed: number;
  /** 0 a seedling, 1 the end of the tree's life. */
  age01: number;
  environment?: TreeEnvironment;
  /**
   * How far this individual is allowed to sit from its species' middle,
   * 0..1 — a dial on the RANGES, not on the seed. At 0 every tree is the
   * species' average and the wood is a plantation; at 1 the full profile is
   * in play. It exists so "is this real variation or noise?" can be answered
   * by turning it down and watching what stops moving.
   */
  variation?: number;
  /**
   * The trait tier. Only `branchCount` and `branchLength` are read here — the
   * other eight axes are expression and are applied to the drawn instance by
   * `render/chunkView.ts`, so applying them at generation too would count
   * them twice. See `PlantPhenotype`'s own note on the split.
   */
  phenotype?: Partial<PlantPhenotype> | null;
}

/** What a grown tree is, besides its triangles. */
export interface GrownTree {
  geometry: THREE.BufferGeometry;
  stage: StageName;
  /** Build-space height and half-width, measured off the result. */
  height: number;
  radius: number;
  triangles: number;
  /** How many branch segments carry wood, and how many masses hang on them. */
  branches: number;
  masses: number;
}

/** One node in the skeleton, kept so the next order can grow out of it. */
interface Node {
  at: [number, number, number];
  /**
   * Where the branch that ends at `at` BEGAN.
   *
   * Foliage is strung back along the branch that grew it, and the first cut
   * walked back along the tip's own direction — which is only the branch for
   * a straight one. Every order here is a `bough` and curves by design, and
   * an oak's primaries sag by 0.22–0.44 radians before they lift, so a mass
   * placed 60% back along the tip tangent sat in open air well off the wood.
   * The chord from `from` to `at` is not the curve either, but it touches it
   * at both ends and never leaves it by more than the sag — which at these
   * lengths is under a mass's own radius.
   */
  from: [number, number, number];
  yaw: number;
  elev: number;
  /** Length of the branch that ended here. */
  len: number;
  radius: number;
  /** 0 the bole, 1 a primary, 2+ below that. */
  order: number;
  /** Is this the leader's own tip? */
  leader: boolean;
  /** Dead wood carries no foliage and is pale. */
  dead: boolean;
}

const TAU = Math.PI * 2;

export function growTree(p: GrowParams): GrownTree {
  const S = p.species;
  const rnd = mulberry32(p.seed | 0);
  const age = clamp(p.age01, 0, 1);
  const env = { ...NEUTRAL_ENVIRONMENT, ...(p.environment ?? {}) };
  const vary = clamp(p.variation ?? 1, 0, 1);

  /**
   * Draw a per-individual value, pulled toward the range's own middle by
   * `variation`. This is the only place the seed and the profile meet.
   */
  const R = (r: { lo: number; hi: number; bias?: number }): number => {
    const full = pick(r, rnd());
    const mid = (r.lo + r.hi) / 2;
    return lerp(mid, full, vary);
  };
  /** Read an age curve. */
  const A = (c: readonly [number, number, number, number]) => curveAt(c, age);

  /* ---- the resolved individual ------------------------------------- */
  // …and this individual's own vigour, which is a fact about the tree rather
  // than about its age: it holds for the whole life, so a suppressed sapling
  // grows into a suppressed ancient.
  const vigour = R(S.vigour);
  const size = A(S.age.size) * vigour;
  const apical = clamp(A(S.age.apical), 0, 1);
  const trunkShare = clamp(R(S.trunkShare) * A(S.age.trunkShare), 0.08, 0.97);
  const girth = A(S.age.girth);
  /*
   * The trait tier. `expressed(v, gain)` is the repository's own normalized
   * axis → factor map (0 is exactly 1, ±1 is the gain and its reciprocal),
   * so neutral multiplies by one and a plant with no phenotype grows the tree
   * its species and seed alone would have grown.
   *
   * The gains are a DESIGN RANGE in the sense `PLANT_EXPRESSION` means it:
   * ±1 has to read as a different plant rather than as this one's far tail,
   * and 1.55 on limb count moves a five-limbed oak to eight or to three.
   */
  const ph = p.phenotype;
  const kCount = ph ? expressed(ph.branchCount ?? 0, 1.55) : 1;
  const kLength = ph ? expressed(ph.branchLength ?? 0, 1.42) : 1;

  const nLimbs = Math.max(1, Math.round(R(S.limbs) * A(S.age.limbs) * kCount));
  const depth = Math.max(0, Math.round(R(S.depth) * A(S.age.depth)));
  const crownW = A(S.age.crownWidth);
  const asym = clamp(R(S.asymmetry) * A(S.age.asymmetry), 0, 1);
  const foliageK = A(S.age.foliage);
  const deadK = clamp(A(S.age.deadwood), 0, 1);

  /*
   * The environment, applied to the resolved values rather than to the
   * profile — a site bends a tree, it does not change the species. Every term
   * is zero at neutral, so an unspecified site costs exactly nothing.
   */
  const exposure = clamp(env.exposure, -1, 1);
  const crowding = clamp(env.crowding, -1, 1);
  const water = clamp(env.water, -1, 1);

  // crowded trees reach up and in; open-grown ones spread
  const riseBias = crowding * 0.30 - water * 0.06;
  // …and the world's own squeeze, pre-compensated. See `buildSqueeze`: a crown
  // designed at uniform scale is stretched or narrowed by `chunk.ts`'s `ratio`
  // before anybody sees it, and the conifer came out a pencil.
  const spread = clamp(crownW * (1 - crowding * 0.24 + water * 0.10), 0.05, 2)
               * S.buildSqueeze;
  // drought thins a crown, a riverbank fills it
  const fullness = clamp(foliageK * (1 + water * 0.18 - Math.abs(exposure) * 0.14), 0.05, 2);

  const trunkR = R(S.trunkRadius) * girth * Math.max(0.35, Math.pow(size, 0.62));
  const height = size;                    // build space: the mature tree is 1
  const boleH = height * trunkShare;
  const lean = R(S.lean) + Math.max(0, exposure) * 0.10;
  const leanYaw = exposure > 0 ? env.windFrom + Math.PI : rnd() * TAU;
  const sinuosity = R(S.sinuosity);
  const asymYaw = rnd() * TAU;

  const parts: THREE.BufferGeometry[] = [];
  const nodes: Node[] = [];
  /**
   * The primary limbs, kept whole — root, tip and length.
   *
   * A `rafted` crown is laid along THESE, not on the tips of whatever they
   * carry. Hanging rafts on every order-2 node gave a cloud: masses scattered
   * through the crown's volume in three dimensions, correctly attached to
   * wood and reading as clutter, because an oak's canopy is not a volume of
   * foliage — it is a roof over a set of radiating arms, with the long low
   * limbs visible in the air beneath it.
   */
  const primaries: Array<{ from: [number, number, number]; tip: [number, number, number]; len: number }> = [];
  let branches = 0;

  /* ================================================================
   * THE BOLE — a chain of segments that leans and wanders.
   * ============================================================== */
  const BOLE_SEGS = 4;
  let at: [number, number, number] = [0, 0, 0];
  let boleYaw = leanYaw;
  let boleElev = Math.PI / 2 - lean;
  const boleNodes: Node[] = [];
  for (let i = 0; i < BOLE_SEGS; i++) {
    const t0 = i / BOLE_SEGS, t1 = (i + 1) / BOLE_SEGS;
    const r0 = trunkR * Math.pow(0.42, t0);
    const r1 = trunkR * Math.pow(0.42, t1);
    const seg = bough(at, boleYaw, boleElev, boleH / BOLE_SEGS, r0, r1,
                      { segments: 1, facets: 8 });
    for (const g of seg.parts) parts.push(part(g, BARK, S.windFlex * 0.30, 0.62, 1, false));
    branches++;
    at = seg.tip;
    boleNodes.push({ at: [...at] as [number, number, number],
                     from: [...seg.parts.length ? at : at] as [number, number, number],
                     yaw: boleYaw,
                     elev: boleElev, len: boleH, radius: r1, order: 0,
                     leader: false, dead: false });
    // it wanders: the bearing drifts and the pitch nods, both small
    boleYaw += (rnd() - 0.5) * sinuosity * 3.2;
    boleElev += (rnd() - 0.5) * sinuosity * 0.9;
  }
  const head = at;

  // …and the flare it stands on
  const flare = R(S.rootFlare);
  if (flare > 1.02) {
    parts.push(part(faceted(new THREE.CylinderGeometry(
      trunkR * 1.02, trunkR * flare, boleH * 0.12, 8, 1, true))
      .translate(0, boleH * 0.06, 0), BARK, 0, 0.50, 0.92, false));
  }

  /* ================================================================
   * THE LEADER — how much tree is left above the bole, and who gets it.
   *
   * This is apical dominance made geometric. At apical 1 the leader takes
   * nearly the whole remaining height and the limbs are short stubs along
   * it: a stick with a bud on top, which is a sapling. At apical 0 there is
   * no leader and the same length is shared out among the primaries: an old
   * broadleaf, which is a competition between equals.
   * ============================================================== */
  const aboveBole = Math.max(0.02, height - boleH);
  /*
   * ---- FORKING ----
   *
   * Below this much apical dominance the trunk does not continue at all: it
   * FORKS, and the primaries that leave the bole's head are co-dominant stems
   * rather than limbs off a spine.
   *
   * Without it a mature oak came out with a thick vertical trunk running from
   * the ground to the top of its crown with branches hung off it — which is a
   * pine's architecture wearing an oak's canopy. An oak's bole is short and
   * heavy and it divides; the thing that makes the silhouette is that there
   * is no centre after the fork. Nothing needed adding to the profile for it,
   * because `apical` already says which species do this and when: a conifer
   * never drops below 0.74 and forks never, an oak reaches 0.04 by maturity
   * and forks always.
   */
  const FORK_APICAL = 0.30;
  const forked = apical < FORK_APICAL;
  const leaderLen = forked ? 0 : aboveBole * lerp(0.24, 0.96, apical);

  if (leaderLen > 0.02) {
    const L = bough(head, boleYaw, boleElev, leaderLen,
                    trunkR * 0.42, trunkR * 0.42 * lerp(0.5, 0.16, apical),
                    { segments: Math.max(1, Math.round(1 + apical * 2)), facets: 6,
                      curve: (rnd() - 0.5) * 0.24, drift: (rnd() - 0.5) * sinuosity * 3 });
    for (const g of L.parts) parts.push(part(g, BARK, S.windFlex * 0.55, 0.66, 1, false));
    branches += L.parts.length;
    nodes.push({ at: L.tip, from: [...head] as [number, number, number],
                 yaw: L.yaw, elev: L.elev, len: leaderLen,
                 radius: trunkR * 0.2, order: 1, leader: true, dead: false });
  }

  /* ================================================================
   * THE PRIMARIES — off the bole and off the leader, over a RANGE of
   * heights, longest and shallowest at the bottom.
   * ============================================================== */
  const reach = R(S.reach) * spread * kLength * Math.max(0.25, Math.pow(size, 0.78));
  const rise = R(S.limbRise) + riseBias;
  const curve = R(S.limbCurve);
  const decay = R(S.lengthDecay);
  const branchOut = R(S.branchOut) * kCount;

  for (let i = 0; i < nLimbs; i++) {
    const u = nLimbs === 1 ? 0.5 : i / (nLimbs - 1);
    /*
     * Where it leaves. Under strong apical dominance the limbs are pushed UP
     * the leader and stay short — a young conifer's whorls. As dominance
     * falls they walk back down onto the bole and lengthen, which is how a
     * crown comes to sit on a trunk rather than along a stick.
     */
    /*
     * Where this limb leaves the trunk. `limbSpan` is the species' own band,
     * as a share of the tree's height: narrow for a tree that forks at one
     * level, wide for one that carries branches the whole way up.
     */
    const band = S.limbSpan;
    // a forked tree divides at ONE place: the head of its bole. An unforked
    // one spreads its limbs over the species' own band.
    const along = forked ? 0.995
                         : band[0] + (band[1] - band[0]) * u + (rnd() - 0.5) * 0.05;
    const alongLeader = forked ? 0 : apical * (0.35 + u * 0.55);
    const from: [number, number, number] = [
      lerp(boleNodes[Math.max(0, Math.min(BOLE_SEGS - 1,
        Math.floor(along * BOLE_SEGS)))].at[0], head[0], alongLeader),
      lerp(boleNodes[Math.max(0, Math.min(BOLE_SEGS - 1,
        Math.floor(along * BOLE_SEGS)))].at[1],
        head[1] + leaderLen * (0.2 + u * 0.6), alongLeader),
      lerp(boleNodes[Math.max(0, Math.min(BOLE_SEGS - 1,
        Math.floor(along * BOLE_SEGS)))].at[2], head[2], alongLeader),
    ];
    // the bearing: evenly about the axis, then pulled toward one side by the
    // crown's own lopsidedness — which age increases
    const even = (i / nLimbs) * TAU + rnd() * 0.7;
    const yaw = lerp(even, asymYaw + (rnd() - 0.5) * (Math.PI * (1 - asym * 0.75)), asym);
    // lower limbs are longer and shallower; upper ones shorter and steeper
    // out of a fork the stems rise before they turn out — that lift is what
    // makes a fork a fork rather than a whorl of spokes
    // …a modest lift. At 0.42 rad the fork stole the crown's width outright —
    // the oak measured 2.34 w:h before and 1.47 after, on a species whose
    // whole identity is being wider than it is tall. A fork rises enough to
    // read as a division and then the limbs go out.
    const elev = (forked ? rise * (0.78 + u * 0.5) + 0.16 : rise * (0.78 + u * 0.5))
               + (rnd() - 0.5) * 0.2;
    const len = reach * (1.18 - u * 0.42) * (0.82 + rnd() * 0.36)
              * lerp(1, 0.34, apical);
    grow({ at: from, from: [...from] as [number, number, number], yaw, elev, len,
           // A CO-DOMINANT stem is thick. Off a spine a primary is half the
           // trunk; out of a fork it is most of it, because between them the
           // forks ARE the trunk from here up — and a fork drawn at limb
           // thickness reads as a pollarded stump with sticks in it.
           radius: trunkR * (forked ? 0.80 : 0.5) * (1 - u * 0.22),
           order: 1, leader: false, dead: false });
  }

  /** Grow one branch and, recursively, the orders below it. */
  function grow(n: Node): void {
    const isLast = n.order > depth;
    const segs = n.order === 1 ? 3 : n.order === 2 ? 2 : 1;
    const facets = n.order === 1 ? 5 : n.order === 2 ? 4 : 3;
    /*
     * Dead wood. Only the OUTER orders die — a dead primary would take a
     * quarter of the crown with it and read as damage rather than as age —
     * and a dead branch is pale, bare and a little straighter, because what
     * killed it also stopped it growing toward anything.
     */
    const dead = n.dead
      || (n.order >= 2 && deadK > 0 && rnd() < deadK * (n.order >= 3 ? 1 : 0.45));
    /*
     * A BRANCH DOES NOT GROW UNDERGROUND.
     *
     * The weeping architecture turns its outer orders over and runs them
     * DOWN, which is the willow — and on a low node a downward bough put its
     * tip below the build's own base, so a willow buried a hand's width of
     * itself wherever the world stood it. Measured at −0.011 on variant 4;
     * `test/phenotype.ts` asks for every vertex at 0.005 or above once the
     * droop morph has had its turn.
     *
     * The elevation is raised just enough that the tip clears the floor.
     * `bough`'s own curve can still dip below the chord, so the floor is set
     * above zero with room for it — and a branch that has to level out to
     * obey this is a branch that has run out of room, which is exactly what a
     * real one does near the ground.
     */
    const FLOOR = 0.03;
    if (n.at[1] > FLOOR && n.len > 1e-4) {
      const need = (FLOOR - n.at[1]) / n.len;
      if (Math.sin(n.elev) < need) n.elev = Math.asin(clamp(need, -1, 1));
    }
    const B = bough(n.at, n.yaw, n.elev, n.len, n.radius, n.radius * 0.34, {
      segments: segs, facets,
      curve: dead ? curve * 0.3 : curve * (n.order === 1 ? 1 : 0.7) + (rnd() - 0.5) * 0.18,
      drift: (rnd() - 0.5) * 0.5,
    });
    const tone = dead ? 1.22 : 1;
    const shade = dead ? 0.86 : 0.72 + n.order * 0.04;
    for (const g of B.parts) {
      // stiffness by ROLE, as a share of the species' own coefficient — a
      // bole barely moves, a twig is nearly all lever (`TREES.md` §6)
      parts.push(part(g, BARK, S.windFlex * (0.5 + n.order * 0.28), shade, tone, false));
    }
    branches += B.parts.length;

    if (n.order === 1 && !dead) {
      primaries.push({ from: [...n.at] as [number, number, number],
                       tip: [...B.tip] as [number, number, number], len: n.len });
    }
    if (isLast) {
      nodes.push({ ...n, at: B.tip, from: [...n.at] as [number, number, number],
                   yaw: B.yaw, elev: B.elev, dead });
      return;
    }
    // …and the order below it, leaving over the outer part of this one
    const kids = Math.max(1, Math.round(branchOut * (n.order === 1 ? 1 : 0.85)));
    for (let k = 0; k < kids; k++) {
      const t = 0.34 + (kids === 1 ? 0.4 : (k / (kids - 1)) * 0.56) + (rnd() - 0.5) * 0.08;
      const kidAt: [number, number, number] = [
        n.at[0] + (B.tip[0] - n.at[0]) * t,
        n.at[1] + (B.tip[1] - n.at[1]) * t,
        n.at[2] + (B.tip[2] - n.at[2]) * t];
      grow({
        at: kidAt,
        from: [...kidAt] as [number, number, number],
        yaw: B.yaw + (k % 2 ? 1 : -1) * (0.45 + rnd() * 0.6),
        /*
         * A weeping tree turns its outer wood OVER and runs it down. Starting
         * that at order 2 meant only the last order hung, on a species where
         * depth is 2 or 3 — so most of the willow was an ordinary broadleaf
         * with a fringe. It starts at the first order below the primaries
         * now, which is where a willow's own architecture turns.
         */
        elev: B.elev + (0.1 + rnd() * 0.36)
              * (S.foliage === 'weeping' && n.order >= 1 ? -2.4 : 1),
        len: n.len * decay * (0.82 + rnd() * 0.36),
        radius: n.radius * 0.42,
        order: n.order + 1,
        leader: false,
        dead,
      });
    }
    // the branch's own tip is a place too
    nodes.push({ ...n, at: B.tip, from: [...n.at] as [number, number, number],
                 yaw: B.yaw, elev: B.elev, dead });
  }

  /* ================================================================
   * FOLIAGE — on the tips, in the species' own style.
   * ============================================================== */
  const clumpR = R(S.clumpSize) * Math.max(0.4, Math.pow(size, 0.7));
  const density = R(S.clumpDensity) * fullness;
  const flatten = R(S.clumpFlatten);
  const stretch = R(S.clumpStretch);
  const tint = R(S.foliageTint);
  const live = nodes.filter((n) => !n.dead);

  let loY = Infinity, hiY = -Infinity, maxR = 1e-3;
  for (const n of live) {
    loY = Math.min(loY, n.at[1]); hiY = Math.max(hiY, n.at[1]);
    maxR = Math.max(maxR, Math.hypot(n.at[0], n.at[2]));
  }
  const span = Math.max(1e-3, hiY - loY);
  const masses: Array<readonly [number, number, number, number, number]> = [];

  /**
   * Put one mass down. Every style funnels through here so the tone hierarchy,
   * the rim grading and the mass bookkeeping are written once — what a style
   * decides is WHERE, and with what proportions.
   */
  const putMass = (
    at: [number, number, number], r: number, order: number,
    opts: { flat?: number; stretch?: number; round?: boolean; tone?: number } = {},
  ) => {
    const u = (at[1] - loY) / span;
    const rim = clamp(Math.hypot(at[0], at[2]) / maxR, 0, 1);
    // tone is a hierarchy, never a scatter (`TREES.md` §5)
    const tone = clamp((opts.tone ?? 1) * (0.76 + u * 0.20 + rim * 0.07) * tint, 0.70, 1.08);
    parts.push(lobe(at, r, (opts.flat ?? flatten) * (0.9 + rnd() * 0.2), rnd() * TAU,
                    tone, S.windFlex * (1.0 + order * 0.16), 0.60,
                    (opts.stretch ?? stretch) * (0.92 + rnd() * 0.18),
                    opts.round ?? true, 'mass'));
    masses.push([at[0], at[1], at[2], r, 0.86]);
  };

  /**
   * ---- THE FIVE FOLIAGE STYLES ----
   *
   * This is where species identity actually lives. `TREES.md` §1 is the table
   * these come from, and its rule generalises: **a family's negative space is
   * where it lives.** Holes THROUGH a broadleaf's crown; shadows WITHIN a
   * conifer's; the limbs showing UNDER an oak's; windows INTO a poplar's
   * column; open air UNDER a willow. Deciding where the holes go decided the
   * tree every time.
   *
   * The first cut of this generator gave all five the `clumped` placement and
   * they came out as five differently-proportioned broadleafs — correct
   * silhouette ratios, no species. Proportion is not identity.
   */
  switch (S.foliage) {
    /*
     * TIERED — a conifer, and it is the one style whose foliage is organised
     * by the TRUNK rather than by the twigs. Levels of pads in rings about the
     * axis, tightening upward into a leader, each ring doubled by a second
     * turned half a step so pads overlap their neighbours and no daylight
     * shows between levels. A conifer wants shadows WITHIN its crown, not
     * holes through it — applying the broadleaf's rule to it is the recorded
     * 2026-08-19 failure that made it read as umbrellas on a pole.
     */
    case 'tiered': {
      const crownBase = boleH * (0.24 + (1 - apical) * 0.34);
      const crownTop = Math.max(crownBase + 0.05, hiY);
      const crownH = crownTop - crownBase;
      /*
       * WHORL COUNT COMES FROM THE SPACING, NOT FROM THE LIMB COUNT.
       *
       * Nine whorls over thirty-five metres of crown is a whorl every four
       * metres, carrying pads two and a half metres across — so the trunk
       * shows between every tier and the tree reads as a pagoda. That is the
       * exact failure `DECISIONS.md` records against the 08-19 conifer:
       * **a broadleaf wants holes THROUGH its crown; a conifer wants shadows
       * WITHIN it**, and the fix then was the same as the fix now — close the
       * tiers until the trunk is glimpsed rather than exposed.
       *
       * So the spacing is set against the pad radius and the count follows
       * from how tall the crown actually is. A sapling still gets two or
       * three because its crown is short, and no age term is needed for it.
       */
      const levels = Math.max(2, Math.min(24,
        Math.round(crownH / Math.max(1e-3, clumpR * 1.0))));
      /*
       * A CONE'S RADIUS IS A SHARE OF ITS OWN HEIGHT.
       *
       * Taking it from `maxR` — how far the branches happened to reach — is
       * what let the conifer come out a bush: cut `reach` by 45% and the
       * measured w:h went 0.54 → 0.45, nowhere near a spire, because the
       * foliage masses themselves are most of the width at this scale. A
       * spruce's crown radius runs about a third of its crown height and that
       * is the number that makes it a spruce, so it is stated rather than
       * inherited. `maxR` still caps it from below: a crown cannot be wider
       * than the wood holding it out.
       */
      const crownR = Math.max(0.02,
        Math.min(maxR * 1.06, crownH * 0.34 * S.buildSqueeze));
      for (let k = 0; k < levels; k++) {
        // spacing tightens toward the top — that taper IS the spire
        const u = Math.pow(k / Math.max(1, levels - 1), 0.78);
        const y = crownBase + u * crownH;
        // widest in the LOWER half, then a long taper to the leader
        const env = u < 0.16 ? lerp(0.86, 1.0, u / 0.16)
                             : Math.pow(1 - (u - 0.16) / 0.84, 1.25);
        const ring = crownR * env * (0.92 + rnd() * 0.16);
        if (ring < 0.006) continue;
        /*
         * Pads per whorl, and it SHRINKS WITH THE TREE.
         *
         * A fixed 4–7 gave a conifer sapling twenty-four foliage masses and
         * 862 triangles — the same whorl the adult wears, three times over,
         * which is a small adult and not a seedling. A young conifer carries
         * a few needles on a few short boughs; the density arrives with the
         * years, along with everything else.
         */
        // …paid for by thinning each whorl. Closing the tiers roughly doubled
        // the level count, and a whorl of six pads at that spacing is mass
        // nobody can see through; four is a bough ring.
        const pads = Math.max(2, Math.round((2 + (1 - u) * 1.6) * lerp(0.5, 1, size)));
        for (let half = 0; half < 2; half++) {
          for (let i = 0; i < pads; i++) {
            const a = ((i + half * 0.5) / pads) * TAU + rnd() * 0.16 + asymYaw * asym * 0.4;
            const rr = ring * (0.86 + rnd() * 0.28);
            /*
             * THE BOUGH DROOPS AT ITS TIP, and that is what makes the cone.
             *
             * The whorl-level droop below (lower boughs hang, upper ones lift)
             * is a real thing and it was not enough: every pad in a ring sat
             * at one height, so a whorl was a flat disc and a stack of flat
             * discs is a pagoda, not a spruce. A conifer bough leaves the
             * trunk near horizontal and bends DOWN along its length, so the
             * outer end of every ring hangs below its inner end — and the
             * envelope the eye reads as "cone" is the line joining those tips.
             *
             * So the droop is radial as well as vertical: a pad's own
             * distance from the axis, as a share of the widest ring, bends it
             * down. The lower whorls are longer and therefore hang further,
             * which deepens the taper for free.
             */
            const tipHang = Math.pow(rr / Math.max(1e-3, crownR), 1.15)
                          * crownH * (0.10 + (1 - u) * 0.10);
            // the lower boughs droop and the upper ones lift
            const droop = lerp(-0.10, 0.05, u) * crownH - tipHang;
            /*
             * A PAD REACHES THE AXIS, AND IT IS NOT A DISC.
             *
             * Two things were keeping the trunk on show. The pads sat at 0.72
             * of the ring radius, so every whorl had a hole in its middle;
             * and at `flat` 0.34 a pad was 0.6 m deep against a 2.1 m gap
             * between whorls, so the tiers could not touch however many there
             * were. `DECISIONS.md` records the same fix on the 08-19 kit — a
             * pad's roof begins on the trunk's own axis a little above its
             * level, so every level is closed at the centre.
             *
             * Pulled in to 0.52 and given real depth, consecutive whorls
             * overlap and the trunk is glimpsed rather than exposed.
             */
            /*
             * …and never below the ground it stands on. The tip droop above
             * is what makes the cone, and on the lowest whorl of a squat
             * variant it carried the skirt 0.069 below the build's own base —
             * past `test/visual-kits.ts`'s 0.06 hair, and a plant that hangs
             * under its base buries or hovers by a fixed share of its height
             * wherever the world puts it. The pad's own half-depth is the
             * floor.
             */
            const padR = clumpR * (0.34 + env * 1.05) * (0.85 + rnd() * 0.3);
            const padY = Math.max(padR * 0.8, y + droop + half * crownH * 0.012);
            putMass([Math.cos(a) * rr * 0.52, padY,
                     Math.sin(a) * rr * 0.52],
                    /*
                     * …and the PAD shrinks with the envelope, or the cone
                     * never converges. Holding the mass radius near constant
                     * while the ring closed gave a green pillar: at the top,
                     * where the ring is nearly nothing, the pad was still
                     * most of a mature pad and the taper simply stopped.
                     * `env` is the cone; the foliage has to obey it.
                     */
                    padR, 2,
                    { flat: 0.72 + rnd() * 0.2, stretch: 1.7 + rnd() * 0.4,
                      round: false, tone: 0.96 + u * 0.08 });
          }
        }
      }
      break;
    }

    /*
     * WEEPING — a willow. The outer orders already turned over and are running
     * DOWNWARD (see the branch grammar), so the masses hang BELOW the node
     * that carries them and are elongated on the vertical rather than the
     * horizontal: a spray, not a ball. Strung down in threes so a branchlet
     * reads as a curtain of its own rather than one blob at the bottom.
     *
     * The open air is UNDER the canopy, so nothing is placed near the axis
     * low down — that space is the tree.
     */
    case 'weeping': {
      for (const n of live) {
        /*
         * The CURTAIN, and its length is the species. At 2.2–3.8 mass radii a
         * willow hung about two and a half metres and read as a broadleaf
         * with a fringe; a willow's whole silhouette is the distance between
         * the crown and the ground it nearly reaches, so the strands run four
         * to eight radii and the ones on the outside of the crown run longest.
         */
        const rim = clamp(Math.hypot(n.at[0], n.at[2]) / maxR, 0, 1);
        const hang = clumpR * (4.0 + rim * 3.2 + rnd() * 1.4);
        /*
         * …and a mass ON the branch as well as hanging from it. A willow is a
         * DOME with curtains falling off it; with every mass in the falls the
         * crown's own top was bare wood and the tree read as a hedge with
         * dangly bits. This is the dome — flattened, sitting on the node — and
         * the sprays below are what makes it a willow.
         */
        putMass([n.at[0], n.at[1] + clumpR * 0.25, n.at[2]],
                clumpR * (0.9 + rnd() * 0.4), n.order,
                { flat: 0.62 + rnd() * 0.2, stretch: 1.1 + rnd() * 0.2 });
        // …and a strand is made LONG, not dense. At 2.2 the willow came out
        // 5,134 triangles for curtains that were already reading; what makes
        // a curtain is its reach downward, which the line above just doubled.
        /*
         * ONE ELONGATED SPRAY, not a column of balls.
         *
         * A strand was two or three masses spaced further apart than they were
         * tall, so a willow's curtain came out as beads on a vertical string —
         * the same failure the clumped crowns had sideways. A willow spray is
         * a single continuous fall of fine foliage; modelling it as one lobe
         * stretched hard down its own length is both what it looks like and a
         * third of the triangles, which matters on the heaviest build in the
         * kit (5,322 before this).
         */
        const count = Math.max(1, Math.round(density * (n.order >= 2 ? 0.75 : 0.4)));
        for (let m = 0; m < count; m++) {
          const t = count === 1 ? 0.5 : m / (count - 1);
          // …and a strand never reaches the ground: the air UNDER a willow is
          // as much the tree as the curtain is
          // …and never below the ground it stands on. A curtain is the willow's
          // whole silhouette and it is allowed to come close; `visual-kits`'s
          // 0.06 hair is what it may not cross, and a spray's own half-depth
          // is the floor that keeps it honest.
          const sprayR = clumpR * (1.0 - t * 0.42) * (0.8 + rnd() * 0.35);
          // A spray is TALLER than it is wide — that is what makes it a spray
          // rather than a ball — so its half-depth is `sprayR × flat`, not
          // `sprayR`. Flooring against the radius alone left the willow 0.063
          // under its own base, which reads as the same bug fixed to two
          // thirds of the way. `flat` is drawn first and the floor uses it.
          // …and it is LONG. 1.7–2.3 left gaps between consecutive sprays; at
          // 3.6–5.4 one spray spans most of its own strand and the fall reads
          // as continuous from the crown to its hem.
          const sprayFlat = 3.6 + rnd() * 1.8;
          const y = Math.max(sprayR * sprayFlat,
                             Math.max(loY * 0.35, n.at[1] - hang * t));
          putMass([n.at[0] + (rnd() - 0.5) * clumpR * 0.7,
                   y,
                   n.at[2] + (rnd() - 0.5) * clumpR * 0.7],
                  sprayR, n.order,
                  // narrow, or an elongated lobe is a pod rather than a spray
                  { flat: sprayFlat, stretch: 0.42 + rnd() * 0.12,
                    tone: 1 - t * 0.14 });
        }
      }
      break;
    }

    /*
     * COLUMNAR — a poplar. Every mass is pulled IN toward the axis, so the
     * crown is a narrow standing column with windows INTO it rather than a
     * crown sitting on a stem. Small masses, stacked, with a few satellites
     * standing off — and no leaf cards at all further down, because under a
     * poplar's own instance ratio a card is a diamond plate standing off the
     * column (`TREES.md` §2).
     */
    case 'columnar': {
      for (const n of live) {
        const count = Math.max(1, Math.round(density * (n.order >= 2 ? 1.2 : 0.6)));
        for (let m = 0; m < count; m++) {
          const back = count === 1 ? 0.2 : (m / count) * 0.5;
          const pull = 0.62;                    // toward the axis
          putMass([n.at[0] * pull + (rnd() - 0.5) * clumpR * 0.5,
                   n.at[1] - n.len * back,
                   n.at[2] * pull + (rnd() - 0.5) * clumpR * 0.5],
                  clumpR * (0.85 + rnd() * 0.4), n.order,
                  { flat: 0.62 + rnd() * 0.16, stretch: 0.92 + rnd() * 0.16 });
        }
      }
      break;
    }

    /*
     * RAFTED — a spreading oak. Flat overlapping masses that lie ALONG the
     * limb and stretch WITH the crown rather than staying round under the
     * instance ratio (Griffin, 2026-08-19: "rounded and wide, not balls").
     * Nothing hangs below the limb line: the negative space is UNDER the
     * canopy, which is what leaves the long low limbs showing against the
     * ground.
     */
    case 'rafted': {
      /*
       * A ROOF OVER RADIATING ARMS.
       *
       * Each primary carries a chain of flat rafts along its own chord, from
       * a third of the way out to just past the tip, sitting ABOVE the limb
       * rather than around it. Neighbouring rafts overlap along the chord and
       * neighbouring arms meet near the crown's middle, so the canopy closes
       * into one broad surface while every limb stays in open air beneath it.
       * That is the oak's negative space — UNDER — and it is the whole species.
       *
       * The sub-branches are not ignored: they get small masses that break the
       * arm's edge, which is what stops a raft chain reading as a plank.
       */
      const arms = primaries.length ? primaries : live.map((n) => ({
        from: n.from, tip: n.at, len: n.len,
      }));
      for (const arm of arms) {
        const dx = arm.tip[0] - arm.from[0], dy = arm.tip[1] - arm.from[1];
        const dz = arm.tip[2] - arm.from[2];
        const runLen = Math.max(1e-3, Math.hypot(dx, dy, dz));
        // Tight enough that consecutive rafts FUSE. At a spacing of 1.15×
        // the raft radius the arm read as a chain of separate plates; a raft
        // is a leaf mass, and the thing it has to look like is continuous.
        const n = Math.max(3, Math.round(runLen / Math.max(1e-3, clumpR * 0.72)));
        for (let i = 0; i < n; i++) {
          const t = 0.30 + (i / Math.max(1, n - 1)) * 0.82;
          const at: [number, number, number] = [
            arm.from[0] + dx * t,
            // …above the limb, and rising a little toward the crown's middle,
            // so the roof is domed rather than a plate laid on sticks
            arm.from[1] + dy * t + clumpR * (0.62 + (1 - t) * 0.5),
            arm.from[2] + dz * t];
          const rim = clamp(Math.hypot(at[0], at[2]) / maxR, 0, 1);
          putMass(at, clumpR * lerp(1.06, 0.66, rim) * (0.9 + rnd() * 0.24), 2,
                  { round: false, tone: 0.98 + (1 - rim) * 0.05 });
        }
      }
      /*
       * …the fringe of small masses on what the limbs carry, to break the
       * arms' edges — a raft chain with a clean outline reads as a plank.
       *
       * And the LEADER is clothed. It is order 1, so the fringe's own rule
       * skipped it and an oak came out with a bare spike standing through the
       * middle of its own canopy. What little leader an oak keeps carries
       * leaves like everything else.
       */
      for (const n of live) {
        /*
         * The leader is clothed ONLY where the species still has one.
         *
         * Clothing it unconditionally gave a spreading oak a point at the top
         * — a cap sitting above a canopy whose whole business is going
         * outward. An oak at maturity has already lost its leader to the
         * competition between its limbs (`apical` 0.04); what little stem
         * stands above the fork is not a spire and takes no cap. Above about
         * a third of dominance there IS a leader and it does.
         */
        if (n.order < 2 && !(n.leader && apical > 0.35)) continue;
        if (n.order >= 2 && rnd() > 0.55) continue;
        putMass([n.at[0], n.at[1] + clumpR * (n.leader ? 0.1 : 0.3), n.at[2]],
                clumpR * (n.leader ? 0.95 + rnd() * 0.3 : 0.42 + rnd() * 0.24),
                Math.max(2, n.order), { round: false, tone: 0.94 });
      }
      break;
    }

    /*
     * CLUMPED — the round broadleaf, and the default. Separated round masses
     * strung back along the twig that grew them, overlapping enough to fuse
     * into an irregular surface and no more, so the holes fall where the
     * branches diverge: through the crown.
     */
    default: {
      /*
       * CLUMPED — a cluster ABOUT the branch, not a row along it.
       *
       * The masses walk the branch's chord, which is right, and the first cut
       * scattered them off it by 0.15 of a mass radius, which is nothing. With
       * the masses shrunk to fix the facet size the spacing between them grew
       * relative to their width, and a broadleaf came out as beads on a
       * string — three or four spheres in a visible line up every twig, like
       * a bunch of grapes.
       *
       * The scatter is PERPENDICULAR to the branch now and about a mass wide,
       * so consecutive masses overlap sideways as well as along, and a twig's
       * worth of foliage fuses into one lump with an irregular edge. A crown
       * is made of lumps; a row of spheres is a diagram of one.
       */
      const _d: [number, number, number] = [0, 0, 0];
      const _u: [number, number, number] = [0, 0, 0];
      const _v: [number, number, number] = [0, 0, 0];
      for (const n of live) {
        const count = Math.max(1, Math.round(density * (n.order >= 2 ? 1 : 0.6)));
        // the branch's own direction, and two axes across it
        _d[0] = n.at[0] - n.from[0]; _d[1] = n.at[1] - n.from[1]; _d[2] = n.at[2] - n.from[2];
        const dl = Math.max(1e-5, Math.hypot(_d[0], _d[1], _d[2]));
        _d[0] /= dl; _d[1] /= dl; _d[2] /= dl;
        // any vector not parallel to the branch gives the first cross axis
        const ax = Math.abs(_d[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
        _u[0] = _d[1] * ax[2] - _d[2] * ax[1];
        _u[1] = _d[2] * ax[0] - _d[0] * ax[2];
        _u[2] = _d[0] * ax[1] - _d[1] * ax[0];
        const ul = Math.max(1e-5, Math.hypot(_u[0], _u[1], _u[2]));
        _u[0] /= ul; _u[1] /= ul; _u[2] /= ul;
        _v[0] = _d[1] * _u[2] - _d[2] * _u[1];
        _v[1] = _d[2] * _u[0] - _d[0] * _u[2];
        _v[2] = _d[0] * _u[1] - _d[1] * _u[0];

        for (let m = 0; m < count; m++) {
          // on the chord of the branch that grew it — see `Node.from`
          const t = count === 1 ? 1 : 1 - (m / count) * 0.5;
          const su = (rnd() - 0.5) * clumpR * 1.7;
          const sv = (rnd() - 0.5) * clumpR * 1.7;
          const at: [number, number, number] = [
            n.from[0] + _d[0] * dl * t + _u[0] * su + _v[0] * sv,
            n.from[1] + _d[1] * dl * t + _u[1] * su + _v[1] * sv,
            n.from[2] + _d[2] * dl * t + _u[2] * su + _v[2] * sv];
          const rim = clamp(Math.hypot(at[0], at[2]) / maxR, 0, 1);
          putMass(at, clumpR * lerp(1.0, 0.78, rim) * (0.82 + rnd() * 0.36), n.order);
        }
      }
      break;
    }
  }

  // the fringe, on the rim alone — where the outline actually is
  if (masses.length) {
    const rim = [...masses]
      .sort((a, b) => (Math.hypot(b[0], b[2]) + b[1] * 0.5)
                    - (Math.hypot(a[0], a[2]) + a[1] * 0.5))
      .slice(0, Math.max(3, Math.round(masses.length * 0.5)));
    const cards = Math.round(clamp(masses.length * 0.4, 4, 40));
    parts.push(leafCards(rim, cards, clumpR * 0.3, (p.seed ^ 0x5eaf) | 0,
                         S.windFlex * 1.25, 0.66));
  }

  const geometry = merge(parts);

  /*
   * ---- NOTHING BELOW THE GROUND IT STANDS ON ----
   *
   * A backstop, and it is stated as one. Every build in this kit is modelled
   * about the plant's base, and `render/chunkView.ts` seats that base on the
   * terrain — so a vertex under y = 0 is a plant that buries a share of
   * itself wherever the world puts it. `test/visual-kits.ts` allows a hair
   * (0.06) and `test/phenotype.ts` wants everything at 0.005 or above once
   * the droop morph has had its turn.
   *
   * The real fixes are upstream and they are the ones that matter: branches
   * are not grown downward past the floor, conifer skirts and willow sprays
   * are floored by their own half-depth. What survives all of that is a
   * `bough`'s curve dipping below its own chord by a few millimetres, which
   * cannot be predicted from the endpoints. Six millimetres on a build a
   * metre tall is 0.6% of its height and nothing on screen; a plant with its
   * foot underground is a defect at every range.
   *
   * `pcen` and `lobe` are deliberately NOT clamped with it. They are offsets
   * rather than positions, the correction is under a millimetre of their own
   * magnitude, and keeping the morphs bit-exact matters more than that.
   */
  {
    const pos = geometry.attributes.position as THREE.BufferAttribute;
    const arr = pos.array as Float32Array;
    const FOOT = 0.006;
    let lifted = 0;
    for (let i = 1; i < arr.length; i += 3) {
      if (arr[i] < FOOT) { arr[i] = FOOT; lifted++; }
    }
    if (lifted) pos.needsUpdate = true;
  }

  geometry.computeBoundingBox();
  const bb = geometry.boundingBox!;
  return {
    geometry,
    stage: stageAt(age),
    height: bb.max.y,
    radius: Math.max(Math.abs(bb.max.x), Math.abs(bb.min.x),
                     Math.abs(bb.max.z), Math.abs(bb.min.z)),
    triangles: geometry.attributes.position.count / 3,
    branches,
    masses: masses.length,
  };
}

/** Foliage colour for a grown tree, so a caller can tint the instance. */
export const foliageTintOf = (S: TreeSpeciesProfile, seed: number): number =>
  pick(S.foliageTint, mulberry32(seed | 0)());

